import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { AnimatedNumber } from '@sorare/core/src/atoms/animations/AnimatedNumber';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { FOOTBALL_LEAGUES_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { metadatas } from '@sorare/core/src/lib/seo/common';
import { flagUrl } from '@sorare/core/src/lib/territories';
import {
  desktopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { sortCompetition } from 'LicensedPartners/PartnersList/FootballCompetitions/sortCompetition';
import { Header } from 'LicensedPartners/PartnersList/Header';
import { Item } from 'LicensedPartners/PartnersList/Item';

import type {
  CoverageQuery,
  CoverageQueryVariables,
} from './__generated__/index.graphql';

const Wrapper = styled.div`
  color: var(--c-white);
  background-color: var(--c-black);
`;

const LeaguesList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  margin-bottom: var(--quadruple-unit);
  gap: var(--double-unit);
  @media ${tabletAndAbove} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const HeaderWrapper = styled(Vertical).attrs({ gap: 0, center: true })`
  width: 100%;
  justify-content: flex-start;
`;

const Count = styled.span`
  --count-font-size: clamp(10rem, calc(30 * var(--vw)), 20rem);
  font-family: var(--brand-font);
  font-size: var(--count-font-size);
  margin-bottom: calc(-0.35 * var(--count-font-size) + var(--double-unit));

  @media ${tabletAndAbove} {
    margin-bottom: calc(-0.35 * var(--count-font-size) + var(--quadruple-unit));
  }
`;

const Licensed = styled.span`
  font-size: clamp(2rem, calc(10 * var(--vw)), 5rem);
  color: var(--c-blue-300);
`;

const Competitions = styled.span`
  font-size: clamp(2rem, calc(10 * var(--vw)), 5rem);
`;

const CoveredCompetitions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: clamp(2rem, calc(10 * var(--vw)), 5rem);
  @media ${tabletAndAbove} {
    align-items: baseline;
    flex-direction: row;
    gap: var(--double-unit);
  }
  & > *:first-child {
    margin-bottom: calc(-2 * var(--unit));
  }
`;

const Subtitle = styled.div`
  color: var(--c-nd-600);
  text-align: center;
  padding: var(--quadruple-unit) 0;
  width: 80%;
  font: var(--t-body-m);
  @media ${tabletAndAbove} {
    font: var(--t-body-l);
    width: 70%;
  }
  @media ${desktopAndAbove} {
    width: 50%;
  }
`;

const FOOTBALL_COMPETITIONS_QUERY = gql`
  query CoverageQuery {
    football {
      leaguesOpenForGameStats {
        slug
        displayName
        pictureUrl
        released
        country {
          slug
        }
      }
    }
  }
` as TypedDocumentNode<CoverageQuery, CoverageQueryVariables>;

export const Coverage = () => {
  useTitleAndDescription(
    metadatas.coverage.title,
    metadatas.coverage.description
  );

  const [searchValue, setSearchValue] = useState('');

  const { formatMessage } = useIntl();

  const { data, loading } = useQuery(FOOTBALL_COMPETITIONS_QUERY);

  const competitions = data?.football?.leaguesOpenForGameStats;

  const competitionsToDisplay = useMemo(() => {
    if (searchValue.trim()) {
      return competitions
        ?.filter(
          c =>
            c.displayName.toLowerCase().includes(searchValue.toLowerCase()) ||
            c.slug.includes(searchValue.toLowerCase())
        )
        .sort(sortCompetition);
    }
    return [...(competitions || [])].sort(sortCompetition);
  }, [competitions, searchValue]);

  return (
    <Wrapper>
      <Container>
        <HeaderWrapper>
          <FormattedMessage
            id="Coverage.title"
            defaultMessage="<a>{count}</a><d><b>covered</b><c>competitions</c></d>"
            values={{
              count: loading ? (
                '10+'
              ) : (
                <AnimatedNumber value={competitions?.length || 0} />
              ),
              a: text => <Count>{text}</Count>,
              b: text => <Licensed>{text}</Licensed>,
              c: text => <Competitions>{text}</Competitions>,
              d: text => <CoveredCompetitions>{text}</CoveredCompetitions>,
            }}
          />
          <Subtitle>
            <FormattedMessage
              id="Coverage.subtitle"
              defaultMessage="We cover dozens of leagues every week, allowing you to follow the performances of your players around the world"
            />
          </Subtitle>
        </HeaderWrapper>
        <Header
          onSearchInputChange={setSearchValue}
          searchPlaceholder={formatMessage({
            id: 'Coverage.footballLeagueSearch',
            defaultMessage: 'Search for a league',
          })}
        />
        {loading ? (
          <LoadingIndicator />
        ) : (
          <LeaguesList id="competition">
            {competitionsToDisplay?.map(competition => {
              return (
                <Item
                  key={competition.slug}
                  name={competition.displayName}
                  to={generatePath(FOOTBALL_LEAGUES_SLUG, {
                    slug: competition.slug,
                  })}
                  logoUrl={
                    competition.pictureUrl
                      ? competition.pictureUrl
                      : flagUrl({ country: competition.country })
                  }
                />
              );
            })}
          </LeaguesList>
        )}
      </Container>
    </Wrapper>
  );
};
